import { type Option } from '~/components/Select/Select';
import { countries } from '~/constants/countries';
import { currencies } from '~/constants/currency';
import { INTEGRATED_STORES } from '~/constants/rate-at-checkout';

import type Rate from './Rate';

import { type JSONAPIAddress, type SavedAPIAddress } from './APIAddress';
import { type OrdersRenderData } from './Order';

enum TypeId {
  FLAT_RATE = 'FLAT_RATE',
  FREE_SHIPPING = 'FREE_SHIPPING',
  LIVE_RATE = 'LIVE_RATE',
}

interface ShippingOptionPayload {
  description: string;
  flat_rate?: number | string;
  flat_rate_currency?: string;
  free_shipping_threshold_currency?: string;
  free_shipping_threshold_min?: number;
  is_active?: boolean;
  is_live_rate?: boolean;
  name: string;
  object_id?: string;
  rate_adjustment?: number | string;
  rate_adjustment_type?: Option | boolean | null;
  service_levels: Maybe<ShippingOptionServiceLevel[]>;
  type: string;
}

interface ShippingOptionInitialValue {
  description: string;
  flat_rate: number | string;
  flat_rate_currency: string;
  flat_rate_fallback?: number | string;
  free_shipping_threshold_currency: string;
  free_shipping_threshold_min?: number | string;
  name: string;
  rate_adjustment: number | string;
  rate_adjustment_type?: Option;
  service_levels?: Option[];
  type: Option | undefined;
}

type RateAtCheckoutErrorObject = {
  [error: string]: string;
};

type RateAtCheckoutErrorArray = string[];

type ShippingOptionServiceLevel = {
  account_object_id: string;
  service_level_token: string;
};

type ParcelTemplateTemplate = {
  api_token: string;
  distance_unit: string;
  height: string;
  is_variable_dimensions: null | string;
  length: string;
  name: string;
  provider_id: number;
  token: string;
  width: string;
};

export interface BaseJSONParcelTemplate {
  distance_unit: string;
  height: Maybe<string>;
  is_default: boolean;
  length: Maybe<string>;
  name: string;
  object_id: string;
  template: ParcelTemplateTemplate | null;
  weight: string;
  weight_unit: string;
  width: string;
}

const fromJSONsenderAddress = (
  jsonAddress: {
    address_id: string;
    country: keyof typeof countries;
    object_owner_id: number;
  } & JSONAPIAddress,
): SavedAPIAddress => ({
  addressId: jsonAddress.address_id,
  city: jsonAddress.city,
  company: jsonAddress.company,
  country: countries[jsonAddress.country],
  email: jsonAddress.email,
  id: jsonAddress.id as number,
  isDefaultReturn: jsonAddress.is_default_return,
  isDefaultSender: jsonAddress.is_default_sender,
  name: jsonAddress.name,
  objectOwnerID: jsonAddress.object_owner_id,
  phone: jsonAddress.phone,
  state: jsonAddress.state,
  street1: jsonAddress.street1,
  street2: jsonAddress.street2,
  streetNumber: jsonAddress.street_no,
  zip: jsonAddress.zip,
});

const currencyFormatToOptions = () => {
  let list: Option[] = [];
  for (const currency in currencies) list.push({ label: currency, value: currency });

  return list;
};

const getCurrencySymbol = (iso: string | undefined) => {
  if (!iso) return;
  return currencies[iso as keyof typeof currencies].symbol;
};

const convertAdjustmentValue = (value: string, type: unknown) => {
  const intVal = +value;
  return type || intVal === 0 ? intVal : intVal * -1;
};

const formValueToPayload = (
  formValues: ShippingOptionInitialValue,
): Maybe<ShippingOptionPayload> => {
  const {
    description,
    flat_rate,
    flat_rate_currency,
    flat_rate_fallback,
    free_shipping_threshold_currency,
    free_shipping_threshold_min,
    name,
    rate_adjustment,
    rate_adjustment_type,
    service_levels,
    type,
  } = formValues;

  const serviceLevel = (service_levels: ShippingOptionInitialValue['service_levels']) => {
    if (!Array.isArray(service_levels)) return [];
    return service_levels.map((el) => {
      const parsedValue = (el.value as string).split('|');
      return { account_object_id: parsedValue[0], service_level_token: parsedValue[1] };
    });
  };

  switch (type?.value) {
    case 'FLAT_RATE':
      return {
        description,
        flat_rate: Number(flat_rate),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        flat_rate_currency: (flat_rate_currency as any).value,
        name,
        // This type seems wrong, `flate_rate_currency` has type of string. Maybe the type is wrong?
        service_levels: serviceLevel(service_levels),
        type: type.value,
      };
    case 'LIVE_RATE':
      // Do it without asserting type or non-nullability?
      const convertedRate = convertAdjustmentValue(
        rate_adjustment as string,
        rate_adjustment_type!.value,
      );
      return {
        description,
        flat_rate: Number(flat_rate_fallback),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        flat_rate_currency: (flat_rate_currency as any)?.value,
        name,
        // This type seems wrong, `flate_rate_currency` has type of string. Maybe the type is wrong?
        rate_adjustment: convertedRate,
        service_levels: serviceLevel(service_levels),
        type: type.value,
      };
    case 'FREE_SHIPPING':
      return {
        description,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        free_shipping_threshold_currency: (free_shipping_threshold_currency as any)?.value,
        free_shipping_threshold_min: Number(free_shipping_threshold_min),
        name,
        // TODO: This type seems wrong, `flate_rate_currency` has type of string. Maybe the type is wrong?
        service_levels: serviceLevel(service_levels),
        type: type.value,
      };
    default:
      return;
  }
};

const fromJSONcarrierTemplate = (template: ParcelTemplateTemplate | null) => {
  if (!template) return null;
  return {
    ...template,
    apiToken: template.api_token,
    distanceUnit: template.distance_unit,
    isVariableDimensions: template.is_variable_dimensions,
    providerId: template.provider_id,
  };
};

const fromJSON = (parcelTemplate: BaseJSONParcelTemplate) => ({
  carrierTemplate: fromJSONcarrierTemplate(parcelTemplate.template),
  distanceUnit: parcelTemplate.distance_unit,
  height: Number(parcelTemplate.height),
  isDefault: parcelTemplate.is_default,
  length: Number(parcelTemplate.length),
  name: parcelTemplate.name,
  objectId: parcelTemplate.object_id,
  weight: Number(parcelTemplate.weight),
  weightUnit: parcelTemplate.weight_unit,
  width: Number(parcelTemplate.width),
});

const parseErrorObject = (errorObj: RateAtCheckoutErrorObject): RateAtCheckoutErrorArray => {
  const errors: RateAtCheckoutErrorArray = [];
  for (const [errorKey, errorMessage] of Object.entries(errorObj))
    if (errorKey && errorMessage) errors.push(`${errorKey}: ${errorMessage}`);

  return errors;
};

/***
@description get Shipment dataset by shipmentId from datasets. batchEntry and order
 */
const getShipmentDatasetById = (shipmentId: string, shipmentDatasets: OrdersRenderData[]) => {
  if (!shipmentDatasets || !shipmentId) return;

  return shipmentDatasets.find((dataset) => {
    if (dataset.batchEntry?.shipmentObjectId === shipmentId) return dataset;
  });
};

/***
@description check if order came from an integrated Rate at Checkout store
 */
const isIntegratedStore = (shopApp?: null | string) => {
  if (!shopApp) return;

  const storeName = shopApp.toLowerCase().trim();
  return INTEGRATED_STORES.includes(storeName);
};

/***
@description return shipping option if it match integrated store methodName.
 */
const getMatchedShippingOption = (
  shippingOptions: ShippingOptionPayload[],
  orderShipmentMethod?: string,
) => {
  const shippingMethodName = orderShipmentMethod?.toLowerCase().trim();
  if (!shippingMethodName || shippingOptions.length === 0) return;

  // find match for selected Store Options by name in existing user Shipping Options
  return shippingOptions.find((option) => {
    const shippingOptionName = option?.name?.toLowerCase().trim();
    return shippingOptionName === shippingMethodName;
  });
};

/***
 @description find matched service levels with rates.
 @description return cheapest matched rate.
 */
const findCheapestMatchedServiceLevelRate = (
  matchedOptionServiceLevels: Maybe<ShippingOptionServiceLevel[]>,
  rates: Rate[],
) => {
  if (!matchedOptionServiceLevels || rates.length === 0) return;
  let cheapestMatchedRate;

  for (const serviceLevel of matchedOptionServiceLevels) {
    // find and return matched service level and carrier account id to Rate
    const matchedRate = rates.find((rate) => {
      const optionAccountId = serviceLevel.account_object_id?.replace(/-/g, '');
      const optionToken = serviceLevel.service_level_token;
      const isMatchedAccount = rate.carrierAccount === optionAccountId;
      const isMatchedToken = rate.servicelevel?.token === optionToken;

      return isMatchedToken && isMatchedAccount;
    });

    // found matched service level token with rate
    if (matchedRate) {
      if (!cheapestMatchedRate) {
        // set first matched Rate
        cheapestMatchedRate = matchedRate;
      } else {
        const matchedRateAmount = parseFloat(matchedRate.amount);
        const cheapestRateAmount = parseFloat(cheapestMatchedRate.amount);
        cheapestMatchedRate =
          matchedRateAmount < cheapestRateAmount ? matchedRate : cheapestMatchedRate;
      }
    }
  }
  return cheapestMatchedRate;
};

export {
  RateAtCheckoutErrorArray,
  ShippingOptionInitialValue,
  ShippingOptionPayload,
  ShippingOptionServiceLevel,
  TypeId,
  currencyFormatToOptions,
  findCheapestMatchedServiceLevelRate,
  formValueToPayload,
  fromJSON,
  fromJSONsenderAddress,
  getCurrencySymbol,
  getMatchedShippingOption,
  getShipmentDatasetById,
  isIntegratedStore,
  parseErrorObject,
};
